import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to export Monofor audit logs to your SIEM',
    metaDescription:
      'Stream sign-in, MFA, PAM, and policy-change events into Splunk, Sentinel, ELK, or Chronicle. Four-step walkthrough with deep-link to docs.',
    heroEyebrow: 'How-to · Audit log → SIEM',
    heroTitle: ['Audit into your SIEM', 'in real time.'],
    heroBody:
      'Stream every Monosign and Monopam event into Splunk, Sentinel, ELK, or Chronicle (over HTTPS webhook, syslog, or Monosync log export). Build the detections your security program needs.',
    badges: ['Webhook / syslog / Monosync', 'HMAC-signed payloads', '~5 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the full docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'Subscribe. Authenticate. Index. Detect.',
    stepsBody:
      'The shape of any Monofor → SIEM pipeline. Specific connectors live in the help center.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Subscribe to the audit stream',
        body: 'In Monosign, set up an audit subscription. Pick the event types you want (sign-in, MFA challenge, policy change, privilege grant) and the delivery target (HTTPS webhook, syslog, or Monosync log export).',
        hint: 'Start broad in dev, narrow in prod; overly chatty subscriptions can drown the SIEM index.',
      },
      {
        title: 'Authenticate the receiver',
        body: 'For webhook delivery, share an HMAC signing secret so the SIEM can verify payloads. For syslog, configure mutual TLS. Monosync log export uses the Monosync auth layer directly.',
      },
      {
        title: 'Parse and index in the SIEM',
        body: "Drop the Monofor field schema into Splunk / Sentinel / ELK / Chronicle. Map user, app, source-IP, factor, and risk-score fields to the SIEM's canonical schema for cross-product correlation.",
        hint: 'Use the same field names your other identity sources use; it pays back at investigation time.',
      },
      {
        title: 'Build the detections',
        body: 'Start with the classics: impossible travel, brute-force, privilege-escalation in PAM, anomalous group membership change. Monofor events carry enough context that most detections write themselves.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Audit & compliance', description: 'Use case: continuous audit readiness.', href: '/solutions/audit-compliance' },
      { label: 'Identity governance', description: 'Entitlement reviews on top of the same data.', href: '/sync/governance' },
      { label: 'For Security Engineers', description: 'The persona that lives in the SIEM.', href: '/for/security-engineers' },
    ],
  },
  tr: {
    metaTitle: "Monofor denetim logları SIEM'e nasıl aktarılır",
    metaDescription:
      "Oturum açma, MFA, PAM ve politika değişikliği olaylarını Splunk, Sentinel, ELK veya Chronicle'a aktarın. Dört adımlık rehber ve doküman bağlantısı.",
    heroEyebrow: 'Rehber · Denetim logu → SIEM',
    heroTitle: ["Denetim logları SIEM'inizde,", 'gerçek zamanlı.'],
    heroBody:
      "Her Monosign ve Monopam olayını HTTPS webhook, syslog veya Monosync log aktarımı üzerinden Splunk, Sentinel, ELK ya da Chronicle'a aktarın. Güvenlik programınızın ihtiyaç duyduğu tespitleri kurun.",
    badges: ['Webhook / syslog / Monosync', 'HMAC imzalı yükler', '~5 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'Abone ol. Doğrula. İndeksle. Tespit et.',
    stepsBody:
      'Her Monofor → SIEM hattının genel şekli. Bağlayıcıya özel sayfalar yardım merkezinde.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: 'Denetim akışına abone olun',
        body: "Monosign'da bir denetim aboneliği kurun. İstediğiniz olay türlerini (oturum açma, MFA doğrulaması, politika değişikliği, ayrıcalık ataması) ve teslim hedefini (HTTPS webhook, syslog veya Monosync log aktarımı) seçin.",
        hint: 'Geliştirme ortamında geniş başlayın, üretimde daraltın; aşırı konuşkan abonelikler SIEM indeksini boğabilir.',
      },
      {
        title: 'Alıcıyı doğrulayın',
        body: "Webhook teslimi için, SIEM'in yükleri doğrulayabilmesi adına bir HMAC imzalama anahtarı paylaşın. Syslog için mutual TLS yapılandırın. Monosync log aktarımı doğrudan Monosync kimlik doğrulama katmanını kullanır.",
      },
      {
        title: "SIEM'de ayrıştırın ve indeksleyin",
        body: "Monofor alan şemasını Splunk / Sentinel / ELK / Chronicle'a ekleyin. Ürünler arası korelasyon için user, app, source-IP, factor ve risk-score alanlarını SIEM'in kanonik şemasına eşleyin.",
        hint: 'Diğer kimlik kaynaklarınızın kullandığı alan adlarının aynısını kullanın; soruşturma anında karşılığını verir.',
      },
      {
        title: 'Tespitleri kurun',
        body: "Klasiklerle başlayın: imkânsız seyahat, brute-force, PAM'de ayrıcalık yükseltme, anormal grup üyeliği değişikliği. Monofor olayları yeterince bağlam taşır; çoğu tespit kendiliğinden yazılır.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Denetim ve uyum', description: 'Kullanım senaryosu: sürekli denetim hazırlığı.', href: '/solutions/audit-compliance' },
      { label: 'Kimlik yönetişimi', description: 'Aynı verinin üzerinde yetki gözden geçirmeleri.', href: '/sync/governance' },
      { label: 'Güvenlik mühendisleri için', description: "SIEM'de yaşayan persona.", href: '/for/security-engineers' },
    ],
  },
}

type Props = { locale: string }

export default function AuditLogSiemExportPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/audit-log-siem-export"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/subscribe-to-audits' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosync/how-to-sync-integration-logs-to-siem-based-on-the-"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
